export interface VideoTemplate {
  id: string
  name: string
  category: 'Product' | 'Lifestyle' | 'Close-up' | 'Dramatic' | 'Minimal'
  description: string
  prompt: string
  aspect_ratio: '9:16' | '1:1' | '3:4'
  duration: 5 | 10 | 15
  negative_prompt: string
}

const BASE_NEGATIVE =
  'blur, distort, low quality, pixelated, grainy, watermark, text overlay, logo, amateur, shaky, jitter, jump cut'

export const VIDEO_TEMPLATES: VideoTemplate[] = [
  {
    id: 'golden-hour-reveal',
    name: 'Golden Hour Reveal',
    category: 'Product',
    description: 'Slow dolly toward product in warm window light',
    aspect_ratio: '9:16',
    duration: 10,
    negative_prompt: BASE_NEGATIVE,
    prompt:
      'Premium product placed on a white marble countertop near a sun-drenched window. Golden hour light streams through sheer linen curtains casting long soft shadows across the surface. Camera executes a slow forward dolly, gradually revealing the product in sharp focus. Fine ambient dust particles drift through the backlight. Smooth continuous motion. Cinematic, luxury product advertisement aesthetic.',
  },
  {
    id: 'hand-lifestyle-pickup',
    name: 'Hand Lifestyle Pick-Up',
    category: 'Lifestyle',
    description: 'Elegant hand reaches in and lifts the product',
    aspect_ratio: '9:16',
    duration: 10,
    negative_prompt: BASE_NEGATIVE,
    prompt:
      'Elegant feminine hand reaches into frame from the left and picks up the product from a clean white marble vanity surface. Camera executes a smooth lateral pull tracking the hand. Blurred warm bokeh in the background reveals a luxury bathroom setting. Soft morning window light. Shallow depth of field. Premium, aspirational lifestyle aesthetic.',
  },
  {
    id: 'studio-360-rotation',
    name: '360° Studio Rotation',
    category: 'Product',
    description: 'Slow spin on clean white surface, studio lighting',
    aspect_ratio: '1:1',
    duration: 5,
    negative_prompt: BASE_NEGATIVE,
    prompt:
      'Product slowly rotates 180 degrees on a clean matte white surface. Camera holds perfectly still in medium close-up. Soft studio lighting with a single large diffused key light source and a subtle rim highlight separating the product from the background. Clean, minimal, editorial product photography in motion. Smooth continuous rotation.',
  },
  {
    id: 'texture-closeup-reveal',
    name: 'Texture Close-Up',
    category: 'Close-up',
    description: 'Macro detail then slow pull-back to full product',
    aspect_ratio: '3:4',
    duration: 10,
    negative_prompt: BASE_NEGATIVE,
    prompt:
      'Macro close-up of the product surface texture and label detail. Camera starts extremely tight then slowly pulls back in a smooth dolly-out to reveal the full product resting on a dark polished slate surface. Dramatic single side light creates sharp shadow contrast. Film-like shallow depth of field that gradually deepens as the camera pulls back. Premium editorial quality.',
  },
  {
    id: 'outdoor-golden-garden',
    name: 'Outdoor Garden Scene',
    category: 'Lifestyle',
    description: 'Product on stone table, soft garden bokeh behind',
    aspect_ratio: '9:16',
    duration: 10,
    negative_prompt: BASE_NEGATIVE,
    prompt:
      'Product sits centered on a weathered white stone outdoor garden table. Lush summer garden in soft focus behind it. Warm afternoon sunlight creates natural caustic light patterns on the table surface. A gentle breeze moves background foliage and wildflowers. Camera makes a slow smooth lateral pan from left to right. Natural, organic, aspirational lifestyle aesthetic.',
  },
  {
    id: 'overhead-flatlay-drop',
    name: 'Overhead Flat-Lay Drop',
    category: 'Minimal',
    description: 'Top-down hand places product on linen with props',
    aspect_ratio: '1:1',
    duration: 5,
    negative_prompt: BASE_NEGATIVE,
    prompt:
      'Overhead flat-lay shot directly from above. Elegant hand gently places the product onto a clean white linen surface surrounded by complementary props — fresh flowers, a small green leaf, a glass of water. Camera angle is perfectly vertical. Soft even diffused studio light with no harsh shadows. Hand withdraws gracefully. Minimal, modern, editorial still-life aesthetic.',
  },
  {
    id: 'luxury-bathroom-vanity',
    name: 'Luxury Bathroom Vanity',
    category: 'Lifestyle',
    description: 'Moody spa ambience, candlelight, polished marble',
    aspect_ratio: '9:16',
    duration: 10,
    negative_prompt: BASE_NEGATIVE,
    prompt:
      'Product placed on a polished black marble bathroom vanity. A large mirror in the background reflects soft warm candlelight. A glass with condensation sits nearby. Camera slowly dollies forward toward the product. Moody warm amber tones, spa-like atmosphere. Shallow depth of field with bokeh candle reflections. High-end luxury beauty aesthetic.',
  },
  {
    id: 'dramatic-spotlight-reveal',
    name: 'Dramatic Spotlight Reveal',
    category: 'Dramatic',
    description: 'Product emerges from darkness under a single spotlight',
    aspect_ratio: '9:16',
    duration: 5,
    negative_prompt: BASE_NEGATIVE,
    prompt:
      'Scene opens on total darkness. A single narrow focused spotlight slowly fades in from directly above, illuminating the product as if materializing out of shadow. Pure black background. Camera holds steady in medium close-up. High contrast between the lit product and the surrounding darkness. Editorial, luxury fragrance advertisement aesthetic. Smooth light fade-in.',
  },
  {
    id: 'wet-surface-reflection',
    name: 'Wet Surface Reflection',
    category: 'Dramatic',
    description: 'Product reflected in wet dark marble at low angle',
    aspect_ratio: '9:16',
    duration: 10,
    negative_prompt: BASE_NEGATIVE,
    prompt:
      'Product standing on a dark reflective wet black marble surface. Its perfect mirror reflection stretches below it. Small water droplets around the base create subtle ripple patterns in the reflection. Camera positioned at a very low angle nearly at surface level angled upward. Moody overcast natural light. Slow smooth forward dolly. Atmospheric, editorial, high-fashion aesthetic.',
  },
  {
    id: 'morning-kitchen-ritual',
    name: 'Morning Kitchen Ritual',
    category: 'Lifestyle',
    description: 'Fresh ingredients scene, early light, camera tracks right',
    aspect_ratio: '3:4',
    duration: 10,
    negative_prompt: BASE_NEGATIVE,
    prompt:
      'Product placed on a clean concrete kitchen countertop beside fresh natural ingredients — sliced citrus, green herbs, a small ceramic bowl. Early morning light streams through large kitchen windows casting warm parallel bands of light across the counter. Soft steam rises from a nearby coffee cup in the background. Camera tracks smoothly left to right revealing the full scene. Warm, inviting, aspirational morning routine aesthetic.',
  },
]

export const getTemplateById = (id: string): VideoTemplate | undefined =>
  VIDEO_TEMPLATES.find((t) => t.id === id)
